import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import WishlistHeart from '../WishlistHeart.jsx';
import Loader from '@bonprix/pattern-library/templates/loader/default';
import AlpiInformation from './AlpiInformation.jsx';
import AlpiIpicSlider from './AlpiIpicSlider.jsx';
import HeroPictureSlider from './HeroPictureSlider.jsx';
import {Constants} from '../view-constants';
import WindowManager from '@core/scripts/components/window-manager';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import NitroEventHelper from '@core/scripts/helper/nitro-event-helper';
import Benefits from './Benefits.jsx';

const CLICK_PRODUCT_NITRO_EVENT_TYPE = 'CLICK_PRODUCT';

export default function Article({
    givenAlpi,
    shopContent,
    positionIndex,
    filterTrackingValue,
    rankingTrackingValue,
    pointOfSaleId,
    latestAlpiIndexLoaded,
    calculateLatestLoadedAlpi,
    navigateToProduct
}) {
    const {wishlistEnabled} = useContext(articleOverviewStaticContext);

    const [activeIndex, setActiveIndex] = useState(0);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const articleContainer = useRef(null);
    const observerRef = useRef(null);
    const firstViewColors = calculateFirstViewColors(givenAlpi.gridItem);

    const isVisible = positionIndex < latestAlpiIndexLoaded;
    const currentColor = firstViewColors[activeIndex];

    useEffect(() => {
        observerRef.current = new IntersectionObserver(handleIntersection);
        if (articleContainer.current) {
            observerRef.current.observe(articleContainer.current);
        }
    }, []);

    function onChangeItem(color) {
        setActiveIndex(firstViewColors.indexOf(color));
    }

    function handleIntersection(entries) {
        entries.forEach((entry) => {
            if (entry.intersectionRatio) {
                calculateLatestLoadedAlpi(positionIndex);
                observerRef.current.unobserve(articleContainer.current);
            }
        });
    }

    const handleClickOnAlpiInformation = useCallback(
        (e) => {
            e.preventDefault();
            trackClick();
            const productUrl = modifyProductUrl(currentColor.url);
            navigateToProduct(productUrl, positionIndex);
        },
        [currentColor, positionIndex, navigateToProduct]
    );

    const handleClickOnHeroPicture = useCallback(
        (e, urlParam, assetId) => {
            e.preventDefault();
            const productUrl = modifyProductUrl(urlParam, assetId);
            trackClick();
            navigateToProduct(productUrl, positionIndex);
        },
        [positionIndex, navigateToProduct]
    );

    const heroPictureOnAuxClick = useCallback((e, linkTag, urlParam, assetId) => {
        if (e.button === 1) {
            trackClick();
        }
        linkTag.href = modifyProductUrl(urlParam, assetId);
    }, []);

    const heroPictureOnContextMenu = useCallback((linkTag, urlParam, assetId) => {
        linkTag.href = modifyProductUrl(urlParam, assetId);
    }, []);

    function trackClick() {
        WindowManager.setSpecificTabDataItem(Constants.POINT_OF_SALE_ID, pointOfSaleId);
        NitroEventHelper.trigger(CLICK_PRODUCT_NITRO_EVENT_TYPE, {productId: givenAlpi.gridItem.id});
    }

    const placeholderClasses = `alpi__content-placeholder${isImageLoaded ? ' is-hidden' : ''}`;
    const clazzes = `alpi ${givenAlpi.classes ? givenAlpi.classes : ''}`;
    const shopId = shopContent?.id || 0;
    const channelId = shopContent?.channelId || 0;

    return (
        <div className={clazzes} ref={articleContainer} jsh-alpi={positionIndex}>
            <div className="alpi__content-wrapper">
                <div className="alpi__content">
                        <HeroPictureSlider
                            url={currentColor.url}
                            isShopContent={shopContent !== null}
                            images={currentColor.images}
                            inView={isVisible}
                            positionIndex={positionIndex}
                            heroPictureOnClick={handleClickOnHeroPicture}
                            heroPictureOnAuxClick={heroPictureOnAuxClick}
                            heroPictureOnContextMenu={heroPictureOnContextMenu}
                            isImageLoaded={isImageLoaded}
                            onImageLoaded={() => setIsImageLoaded(true)}
                        />
                    {wishlistEnabled && (
                        <WishlistHeart
                            digistyleId={givenAlpi.gridItem.id}
                            articleNumber={currentColor.itemNumber}
                            promotion={currentColor.promotion}
                            itemId={currentColor.id}
                            shopId={shopId}
                            origin={!!shopContent ? Constants.LANDMARK_PAGETYPE_AOP : Constants.LANDMARK_PAGETYPE_SRP}
                            articlePrice={currentColor.price.value}
                            masterStyleId={givenAlpi.gridItem.masterstyleId}
                        />
                    )}

                    <AlpiIpicSlider
                        firstViewColors={firstViewColors}
                        activeIndex={activeIndex}
                        shopId={shopId}
                        channelId={channelId}
                        onChangeItem={onChangeItem}
                        inView={isVisible}
                    />
                    {currentColor.benefits && <Benefits benefits={currentColor.benefits} />}
                    <AlpiInformation
                        title={givenAlpi.gridItem.title}
                        fallback={currentColor.price.fallback}
                        formerPrice={currentColor.price.formerPrice}
                        formattedPrice={currentColor.price.formattedPrice}
                        uniquePrice={currentColor.price.uniquePrice}
                        reduction={currentColor.price.reduction}
                        packItemsCount={currentColor.price.packItemsCount}
                        pricePerItem={currentColor.price.pricePerItem?.formattedPrice}
                        onAlpiInformationClick={handleClickOnAlpiInformation}
                    />
                </div>
                <div className={placeholderClasses}>
                    <Loader className="alpi__content-placeholder-loader" />
                </div>
            </div>
        </div>
    );

    function calculateFirstViewColors(gridItem) {
        const allColors = gridItem.styles.flatMap((style) => {
            return style.colors;
        });
        return gridItem.firstViewColorIDs
            .map((id) => {
                return allColors.find((color) => {
                    return color.id === id;
                });
            })
            .filter((color) => {
                return !!color;
            });
    }

    function modifyProductUrl(urlParam, assetId) {
        const shopId = shopContent ? shopContent.id : 0;
        const preselection = givenAlpi.gridItem.features.includes(Constants.ALPI_FEATURE_PRESELECTION);
        const itemOptionId = preselection ? currentColor.sizes[0].id : null;

        const productUrl = new URL(`${window.location.origin}${urlParam}`);
        const productParams = new URLSearchParams(productUrl.search);
        const currentUrlParams = new URLSearchParams(window.location.search);
        const preview = currentUrlParams.get(Constants.SEARCH_PARAMS_PREVIEW);
        const dateOverride = currentUrlParams.get(Constants.SEARCH_PARAMS_DATE_OVERRIDE);
        const timeOverride = currentUrlParams.get(Constants.SEARCH_PARAMS_TIME_OVERRIDE);
        if(preview && dateOverride && timeOverride) {
            productParams.set(Constants.SEARCH_PARAMS_PREVIEW, preview);
            productParams.set(Constants.SEARCH_PARAMS_DATE_OVERRIDE,dateOverride);
            productParams.set(Constants.SEARCH_PARAMS_TIME_OVERRIDE,timeOverride);
        }
        productParams.set(Constants.SEARCH_PARAMS_SHOP_ID, shopId);
        if (assetId) {
            productParams.set(Constants.SEARCH_PARAMS_ASSET_ID, assetId);
        }
        if (itemOptionId) {
            productParams.set(Constants.SEARCH_PARAMS_ITEM_OPTION_ID, itemOptionId);
        }
        if (!!filterTrackingValue) {
            productParams.set(Constants.SEARCH_PARAMS_FILTER_TRACKING_VALUE, filterTrackingValue);
        }
        if (!!rankingTrackingValue) {
            productParams.set(Constants.SEARCH_PARAMS_RANKING, rankingTrackingValue);
        }
        productUrl.search = productParams;

        return productUrl.toString();
    }
}
